import { useState, useEffect, useMemo, type DragEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { fetchClients } from "@/lib/clientsApi";
import { createPartner, fetchPartners } from "@/lib/partnersApi";
import { listUsers } from "@/lib/usersApi";
import { createProposal } from "@/lib/proposalsApi";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, UserPlus, Mail, Phone, MoreHorizontal, Star, StarOff, X } from "lucide-react";
import ClientForm from "@/components/clients/ClientForm";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { COUNTRY_OPTIONS } from "@/lib/countries";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const createProposalSchemaBase = z
  .object({
    partnerId: z.number().nullable().optional(),
    clientId: z.number({
      required_error: "Client is required",
    }),
    title: z.string().min(1, "Title is required"),
    priority: z.enum(["high", "medium", "low"]),
    proposalCategory: z.enum(["individual", "family", "business"], {
      required_error: "Proposal category is required",
    }),
    sector: z.enum(["tourism", "hotels", "transport", "events", "esg", "sports"]).optional(),
    ownerId: z.string().optional(),
    contextObservations: z.string().optional(),
    mainDestination: z.string().min(1, "Main destination is required"),
  })
  .superRefine((data, ctx) => {
    if (data.proposalCategory === "business" && !data.sector) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Sector is required",
        path: ["sector"],
      });
    }
  });

const createProposalSchemaWithOwner = createProposalSchemaBase.refine((d) => !!d.ownerId?.trim(), {
  message: "Owner is required",
  path: ["ownerId"],
});

type CreateProposalFormData = z.infer<typeof createProposalSchemaBase>;
type PartnerContactForm = {
  name?: string | null;
  position?: string | null;
  email?: string | null;
  telephone?: string | null;
  is_primary?: boolean | null;
  no_response?: boolean | null;
};

const CreateNewProposal = () => {
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const [clients, setClients] = useState<any[]>([]);
  const [partners, setPartners] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [showPartnerForm, setShowPartnerForm] = useState(false);
  const [creatingPartner, setCreatingPartner] = useState(false);
  const [newPartnerName, setNewPartnerName] = useState("");
  const [newPartnerBusinessType, setNewPartnerBusinessType] = useState<"operator" | "agency" | "">("");
  const [newPartnerCountry, setNewPartnerCountry] = useState("");
  const [newPartnerAddress, setNewPartnerAddress] = useState("");
  const [partnerContacts, setPartnerContacts] = useState<PartnerContactForm[]>([]);
  const [addingContact, setAddingContact] = useState(false);
  const [newContact, setNewContact] = useState<PartnerContactForm>({
    name: "",
    position: "",
    email: "",
    telephone: "",
    is_primary: false,
    no_response: false,
  });
  const [showAllPartnerContacts, setShowAllPartnerContacts] = useState(false);
  const [ownerSelectValue, setOwnerSelectValue] = useState<string>("");

  const isSales = userRole === "sales_staff";
  const canAssignOwner = userRole === "ceo" || userRole === "general_manager";
  const schema = canAssignOwner ? createProposalSchemaWithOwner : createProposalSchemaBase;

  const form = useForm<CreateProposalFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      priority: "medium",
      partnerId: null,
      contextObservations: "",
    },
  });

  const selectedPartnerId = form.watch("partnerId");
  const selectedProposalCategory = form.watch("proposalCategory");
  const showSectorField = selectedProposalCategory === "business";

  const priorityPartnerContactIndex = useMemo(
    () => partnerContacts.findIndex((contact) => Boolean(contact.is_primary)),
    [partnerContacts],
  );

  const priorityPartnerContact = useMemo(
    () => (priorityPartnerContactIndex >= 0 ? partnerContacts[priorityPartnerContactIndex] : null),
    [partnerContacts, priorityPartnerContactIndex],
  );

  const otherPartnerContactEntries = useMemo(
    () =>
      partnerContacts
        .map((contact, index) => ({ contact, index }))
        .filter((entry) => !entry.contact.is_primary),
    [partnerContacts],
  );

  const visibleOtherPartnerContactEntries = useMemo(
    () => (showAllPartnerContacts ? otherPartnerContactEntries : otherPartnerContactEntries.slice(0, 5)),
    [otherPartnerContactEntries, showAllPartnerContacts],
  );

  useEffect(() => {
    if (userRole !== "ceo" && userRole !== "general_manager" && userRole !== "sales_staff") {
      navigate("/proposals");
    }
  }, [userRole, navigate]);

  const loadClients = async (partnerId?: number | null) => {
    try {
      const response = await fetchClients({
        page: 1,
        size: 2000,
        sortField: "first_name",
        sortOrder: "asc",
        partnerId: typeof partnerId === "number" ? partnerId : undefined,
      });
      const items = response.items || [];
      if (typeof partnerId === "number") {
        setClients(items);
      } else {
        setClients(items.filter((item: any) => item.partner_id == null));
      }
    } catch (error: any) {
      toast({
        title: "Error fetching clients",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    loadClients(selectedPartnerId ?? null);
  }, [selectedPartnerId]);

  useEffect(() => {
    if (selectedProposalCategory === "business") {
      return;
    }
    if (form.getValues("sector") !== undefined) {
      form.setValue("sector", undefined, { shouldValidate: true });
    }
    form.clearErrors("sector");
  }, [selectedProposalCategory, form]);

  const loadPartners = async () => {
    try {
      const response = await fetchPartners({ page: 1, size: 2000, sortField: "name", sortOrder: "asc" });
      setPartners(response.items || []);
    } catch (error: any) {
      toast({
        title: "Error fetching partners",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    loadPartners();
  }, []);

  useEffect(() => {
    if (!canAssignOwner) return;

    const fetchEmployees = async () => {
      try {
        const users = await listUsers();
        setEmployees(users || []);
      } catch (error: any) {
        toast({
          title: "Error fetching employees",
          description: error.message,
          variant: "destructive",
        });
      }
    };

    fetchEmployees();
  }, [canAssignOwner]);

  const onSubmit = async (data: CreateProposalFormData) => {
    setLoading(true);

    const ownerId = isSales ? user?.id : data.ownerId;
    if (!ownerId) {
      toast({
        title: "Error",
        description: "Owner is required.",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    try {
      const createdProposal = await createProposal({
        client_id: data.clientId,
        partner_id: data.partnerId ?? null,
        title: data.title,
        main_destination: data.mainDestination,
        status: "to_do",
        priority: data.priority,
        proposal_category: data.proposalCategory,
        sector: data.proposalCategory === "business" ? (data.sector ?? null) : null,
        owner_id: ownerId,
        language: "en",
        context_observations: data.contextObservations || null,
        pax_adults: 1,
        pax_children: 0,
        pax_infants: 0,
        passenger_mode_detailed: false,
        last_step: 1,
      });

      toast({
        title: "Proposal created",
      });

      if (createdProposal?.id) {
        navigate(`/proposals/${createdProposal.id}`);
        return;
      }
      navigate("/proposals");
    } catch (error: any) {
      toast({
        title: "Error creating proposal",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClientCreated = (newClient?: any) => {
    if (newClient?.id) {
      if ((newClient.partner_id ?? null) !== (selectedPartnerId ?? null)) {
        return;
      }
      setClients((prev) => {
        const exists = prev.some((c) => c.id === newClient.id);
        return exists ? prev : [...prev, newClient];
      });
      form.setValue("clientId", Number(newClient.id), { shouldValidate: true });
      setShowClientForm(false);
    } else {
      const refreshClients = async () => {
        try {
          await loadClients(selectedPartnerId ?? null);
        } catch {
          setClients([]);
        }
      };
      refreshClients();
    }
  };

  const resetPartnerForm = () => {
    setNewPartnerName("");
    setNewPartnerBusinessType("");
    setNewPartnerCountry("");
    setNewPartnerAddress("");
    setPartnerContacts([]);
    setShowAllPartnerContacts(false);
    setAddingContact(false);
    setNewContact({
      name: "",
      position: "",
      email: "",
      telephone: "",
    });
  };

  const handleAddPartnerContact = () => {
    const trimValue = (value?: string | null) => {
      const trimmed = value?.trim();
      return trimmed ? trimmed : null;
    };

    const candidate: PartnerContactForm = {
      name: trimValue(newContact.name),
      position: trimValue(newContact.position),
      email: trimValue(newContact.email),
      telephone: trimValue(newContact.telephone),
      is_primary: false,
      no_response: false,
    };

    if (!candidate.name && !candidate.email && !candidate.telephone) {
      toast({
        title: "Missing contact info",
        description: "Add at least a name, email, or phone.",
        variant: "destructive",
      });
      return;
    }

    const shouldBePrimary = partnerContacts.length === 0;
    const normalizedCandidate: PartnerContactForm = {
      ...candidate,
      is_primary: shouldBePrimary,
    };
    setPartnerContacts((prev) => [...prev, normalizedCandidate]);

    setAddingContact(false);
    setNewContact({
      name: "",
      position: "",
      email: "",
      telephone: "",
    });
  };

  const setPartnerPriorityContactByIndex = (index: number | null) => {
    setPartnerContacts((prev) =>
      prev.map((contact, i) => ({
        ...contact,
        is_primary: index !== null ? i === index : false,
      })),
    );
  };

  const updatePartnerContactNoResponseByIndex = (index: number, value: boolean) => {
    setPartnerContacts((prev) =>
      prev.map((contact, i) =>
        i === index
          ? {
              ...contact,
              no_response: value,
            }
          : contact,
      ),
    );
  };

  const removePartnerContactByIndex = (index: number) => {
    setPartnerContacts((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length > 0 && !next.some((contact) => Boolean(contact.is_primary))) {
        next[0] = { ...next[0], is_primary: true };
      }
      return next;
    });
  };

  const handlePartnerContactDragStart =
    (list: "priority" | "other", index: number) => (event: DragEvent<HTMLDivElement>) => {
      event.dataTransfer.setData("text/plain", `${list}:${index}`);
      event.dataTransfer.effectAllowed = "move";
    };

  const handlePartnerContactDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const resolvePartnerContactDragPayload = (event: DragEvent<HTMLDivElement>) => {
    const raw = event.dataTransfer.getData("text/plain");
    if (!raw) return null;
    const [list, indexRaw] = raw.split(":");
    const parsed = indexRaw ? Number(indexRaw) : NaN;
    if ((list !== "priority" && list !== "other") || Number.isNaN(parsed)) {
      return null;
    }
    return { list: list as "priority" | "other", index: parsed };
  };

  const handleDropToPartnerPriority = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const payload = resolvePartnerContactDragPayload(event);
    if (!payload) return;
    if (payload.list === "other") {
      setPartnerPriorityContactByIndex(payload.index);
    }
  };

  const handleDropToPartnerOther = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const payload = resolvePartnerContactDragPayload(event);
    if (!payload) return;
    if (payload.list === "priority") {
      setPartnerPriorityContactByIndex(null);
    }
  };

  const handleCreatePartner = async () => {
    const name = newPartnerName.trim();
    if (!name) {
      toast({
        title: "Validation error",
        description: "Partner name is required.",
        variant: "destructive",
      });
      return;
    }
    if (!newPartnerBusinessType) {
      toast({
        title: "Validation error",
        description: "Business type is required.",
        variant: "destructive",
      });
      return;
    }

    try {
      setCreatingPartner(true);
      const created = await createPartner({
        name,
        business_type: newPartnerBusinessType,
        country: newPartnerCountry.trim() || null,
        address: newPartnerAddress.trim() || null,
        contacts: partnerContacts.map((contact) => ({
          name: contact.name?.trim() || null,
          position: contact.position?.trim() || null,
          email: contact.email?.trim() || null,
          telephone: contact.telephone?.trim() || null,
          is_primary: Boolean(contact.is_primary),
          no_response: Boolean(contact.no_response),
        })),
      } as any);

      await loadPartners();
      form.setValue("partnerId", Number(created.id), { shouldValidate: true });
      form.setValue("clientId", undefined as unknown as number, { shouldValidate: true });
      setShowPartnerForm(false);
      resetPartnerForm();

      toast({
        title: "Success",
        description: "Partner created successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error creating partner",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setCreatingPartner(false);
    }
  };

  const clientOptions = clients.map((client) => ({
    id: client.id,
    title: `${client.first_name} ${client.last_name}${
      client.company ? ` - ${client.company}` : ""
    }`,
    description: client.email || "",
  }));

  const partnerOptions = partners.map((partner) => ({
    id: partner.id,
    title: partner.name,
    description: partner.country || "",
  }));
  const sideActionButtonClass = "h-10 w-full justify-center overflow-hidden text-ellipsis whitespace-nowrap sm:w-44 sm:min-w-44";
  const fieldActionRowClass = "grid gap-2 sm:grid-cols-[minmax(0,1fr)_11rem] sm:items-end";
  partnerOptions.unshift({
    id: "none",
    title: "No Partner (Final Client)",
    description: "",
  });

  return (
    <DashboardLayout>
      <div className="mx-auto w-full max-w-[1240px] space-y-6">
        <div>
          <h1 className="text-3xl font-bold">New Proposal</h1>
          <p className="text-muted-foreground">
            Start a new proposal opportunity
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
              <Card>
                <CardHeader>
                  <CardTitle>Client Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="partnerId"
                    render={({ field }) => (
                      <FormItem className="min-w-0">
                        <FormLabel>Partner (optional)</FormLabel>
                        <div className={fieldActionRowClass}>
                          <FormControl>
                            <div className="w-full min-w-0">
                              <SearchableSelect
                                items={partnerOptions}
                                value={field.value == null ? "none" : field.value.toString()}
                                onValueChange={(value) => {
                                  const next = value === "none" ? null : Number(value);
                                  form.setValue("partnerId", Number.isNaN(next as number) ? null : next, {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                    shouldValidate: true,
                                  });
                                  form.setValue("clientId", undefined as unknown as number, { shouldValidate: true });
                                }}
                                placeholder="Select a partner company..."
                                emptyText="No partners found"
                              />
                            </div>
                          </FormControl>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowPartnerForm(true)}
                            className={`${sideActionButtonClass} sm:self-end`}
                          >
                            <UserPlus className="mr-2 h-4 w-4" />
                            New Partner
                          </Button>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="clientId"
                    render={({ field }) => (
                      <FormItem className="min-w-0">
                        <FormLabel>Client *</FormLabel>
                        <div className={fieldActionRowClass}>
                          <FormControl>
                            <div className="w-full min-w-0">
                              <SearchableSelect
                                items={clientOptions}
                                value={field.value?.toString()}
                                onValueChange={(value) => {
                                  const parsed = typeof value === "number" ? value : Number(value);
                                  if (Number.isNaN(parsed)) {
                                    form.setValue("clientId", undefined as unknown as number, {
                                      shouldDirty: true,
                                      shouldTouch: true,
                                      shouldValidate: true,
                                    });
                                    return;
                                  }
                                  field.onChange(parsed);
                                  form.setValue("clientId", parsed as number, {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                    shouldValidate: true,
                                  });
                                  form.clearErrors("clientId");
                                }}
                                placeholder={
                                  selectedPartnerId
                                    ? "Search client inside selected partner..."
                                    : "Search final client by name, company, or email..."
                                }
                                emptyText="No clients found"
                              />
                            </div>
                          </FormControl>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowClientForm(true)}
                            className={`${sideActionButtonClass} sm:self-end`}
                          >
                            <UserPlus className="mr-2 h-4 w-4" />
                            {selectedPartnerId ? "New Partner Client" : "New Final Client"}
                          </Button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                <FormField
                  control={form.control}
                  name="contextObservations"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Context / Observations</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Add any relevant context or client history..."
                          className="min-h-28 max-h-40 resize-y"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Proposal Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>Proposal Title *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., Dubai Trip - Silva Family - July 2026"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="mainDestination"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Main Destination *</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., Dubai, Abu Dhabi" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="priority"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Priority</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select priority" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="proposalCategory"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Proposal Category *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select proposal category" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="individual">Individual</SelectItem>
                            <SelectItem value="family">Family</SelectItem>
                            <SelectItem value="business">Business</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {showSectorField && (
                    <FormField
                      control={form.control}
                      name="sector"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Sector *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select sector" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="tourism">Tourism</SelectItem>
                              <SelectItem value="hotels">Hotels</SelectItem>
                              <SelectItem value="transport">Transport</SelectItem>
                              <SelectItem value="events">Events</SelectItem>
                              <SelectItem value="esg">ESG</SelectItem>
                              <SelectItem value="sports">Sports</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {canAssignOwner && (
              <Card className="w-full xl:max-w-[608px]">
                <CardHeader>
                  <CardTitle>Responsibility</CardTitle>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="ownerId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Assign Owner *</FormLabel>
                        <Select
                          onValueChange={(val) => {
                            setOwnerSelectValue(val);
                            field.onChange(val);
                          }}
                          value={ownerSelectValue}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select responsible person" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {employees.map((employee) => (
                              <SelectItem key={employee.id} value={employee.id}>
                                {employee.full_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/proposals")}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Creating..." : "Create Proposal"}
              </Button>
            </div>
          </form>
        </Form>

        <ClientForm
          open={showClientForm}
          onOpenChange={setShowClientForm}
          onCreate={handleClientCreated}
          defaultPartnerId={selectedPartnerId ?? null}
          forceFinalClient={!selectedPartnerId}
          showClientTypes={false}
        />
        <Dialog
          open={showPartnerForm}
          onOpenChange={(open) => {
            setShowPartnerForm(open);
            if (!open) resetPartnerForm();
          }}
        >
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Partner</DialogTitle>
              <DialogDescription>
                Create a new partner company to associate clients with it.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="rounded-lg border border-border bg-card p-4 space-y-4">
                <div className="space-y-2">
                  <Label>Partner Name *</Label>
                  <Input
                    value={newPartnerName}
                    onChange={(e) => setNewPartnerName(e.target.value)}
                    placeholder="e.g., Luxury Travel Co."
                    disabled={creatingPartner}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Business Type *</Label>
                  <Select
                    value={newPartnerBusinessType || undefined}
                    onValueChange={(value) => setNewPartnerBusinessType(value as "operator" | "agency")}
                  >
                    <SelectTrigger disabled={creatingPartner}>
                      <SelectValue placeholder="Select business type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="operator">Operator</SelectItem>
                      <SelectItem value="agency">Agency</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Country</Label>
                    {newPartnerCountry && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => setNewPartnerCountry("")}
                        disabled={creatingPartner}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                  <SearchableSelect
                    items={COUNTRY_OPTIONS}
                    value={newPartnerCountry || undefined}
                    onValueChange={(value) => setNewPartnerCountry(String(value))}
                    placeholder="Select a country"
                    emptyText="No country found."
                    fallbackDisplayValue={newPartnerCountry || undefined}
                    disabled={creatingPartner}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={newPartnerAddress}
                    onChange={(e) => setNewPartnerAddress(e.target.value)}
                    placeholder="Street, city..."
                    disabled={creatingPartner}
                  />
                </div>
              </div>

              <div className="rounded-lg border border-border bg-card p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold">Contacts</p>
                    <p className="text-xs text-muted-foreground">
                      {partnerContacts.length} total · Drag a contact to set priority.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={creatingPartner}
                    onClick={() => setAddingContact(true)}
                    className="shadow-sm hover:shadow"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add contact
                  </Button>
                </div>

                {addingContact && (
                  <div className="rounded-md border border-border bg-background p-3 space-y-3">
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={creatingPartner}
                        className="h-7 w-7"
                        onClick={() => {
                          setAddingContact(false);
                          setNewContact({
                            name: "",
                            position: "",
                            email: "",
                            telephone: "",
                          });
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Input
                        value={newContact.name ?? ""}
                        onChange={(e) => setNewContact((prev) => ({ ...prev, name: e.target.value }))}
                        placeholder="Contact name"
                        disabled={creatingPartner}
                      />
                      <Input
                        value={newContact.position ?? ""}
                        onChange={(e) => setNewContact((prev) => ({ ...prev, position: e.target.value }))}
                        placeholder="Position"
                        disabled={creatingPartner}
                      />
                      <Input
                        value={newContact.email ?? ""}
                        onChange={(e) => setNewContact((prev) => ({ ...prev, email: e.target.value }))}
                        placeholder="Email"
                        disabled={creatingPartner}
                      />
                      <Input
                        value={newContact.telephone ?? ""}
                        onChange={(e) => setNewContact((prev) => ({ ...prev, telephone: e.target.value }))}
                        placeholder="Phone"
                        disabled={creatingPartner}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={creatingPartner}
                        onClick={() => {
                          setAddingContact(false);
                          setNewContact({
                            name: "",
                            position: "",
                            email: "",
                            telephone: "",
                          });
                        }}
                      >
                        Cancel
                      </Button>
                      <Button type="button" size="sm" disabled={creatingPartner} onClick={handleAddPartnerContact}>
                        Save contact
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <span>Priority contact</span>
                  <span>{priorityPartnerContact ? "1/1" : "0/1"}</span>
                </div>

                <div
                  className="space-y-3"
                  onDragOver={handlePartnerContactDragOver}
                  onDrop={handleDropToPartnerPriority}
                >
                  {!priorityPartnerContact ? (
                    <div className="rounded-md border border-dashed border-border/70 bg-muted/20 p-4 text-sm text-muted-foreground">
                      Drop a contact here to set priority.
                    </div>
                  ) : (
                    <div
                      className="rounded-md border border-border p-3 bg-muted/10"
                      draggable
                      onDragStart={handlePartnerContactDragStart("priority", priorityPartnerContactIndex)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">{priorityPartnerContact.name || "Unnamed contact"}</p>
                            {priorityPartnerContact.no_response && (
                              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-[10px]">
                                No response
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">{priorityPartnerContact.position || ""}</p>
                          <div className="mt-2 text-xs text-muted-foreground space-y-1">
                            <p className="flex items-center gap-2">
                              <Mail className="h-3 w-3" />
                              {priorityPartnerContact.email || "No email"}
                            </p>
                            <p className="flex items-center gap-2">
                              <Phone className="h-3 w-3" />
                              {priorityPartnerContact.telephone || "No phone"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={creatingPartner}
                            onClick={() => setPartnerPriorityContactByIndex(null)}
                            className="shadow-sm hover:shadow"
                          >
                            <StarOff className="mr-2 h-4 w-4" />
                            Remove priority
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button type="button" variant="ghost" size="icon" disabled={creatingPartner} className="h-9 w-9">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                  onClick={() =>
                                    updatePartnerContactNoResponseByIndex(
                                      priorityPartnerContactIndex,
                                      !priorityPartnerContact.no_response,
                                    )
                                  }
                              >
                                {priorityPartnerContact.no_response ? "Clear no response" : "Mark no response"}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => removePartnerContactByIndex(priorityPartnerContactIndex)}
                              >
                                Delete contact
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="h-px bg-border/70" />

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold">Other contacts</p>
                    <p className="text-xs text-muted-foreground">
                      {otherPartnerContactEntries.length} total
                      {otherPartnerContactEntries.length > 5 ? ` · Showing ${visibleOtherPartnerContactEntries.length}` : ""}
                    </p>
                  </div>
                </div>
                {otherPartnerContactEntries.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No other contacts.</p>
                ) : (
                  <div
                    className="space-y-3 max-h-[360px] overflow-y-auto pr-1"
                    onDragOver={handlePartnerContactDragOver}
                    onDrop={handleDropToPartnerOther}
                  >
                    {visibleOtherPartnerContactEntries.map((entry) => (
                      <div
                        key={`new-partner-contact-${entry.index}`}
                        className="rounded-md border border-border p-3 bg-muted/10"
                        draggable
                        onDragStart={handlePartnerContactDragStart("other", entry.index)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium">{entry.contact.name || "Unnamed contact"}</p>
                              {entry.contact.no_response && (
                                <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-[10px]">
                                  No response
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">{entry.contact.position || ""}</p>
                            <div className="mt-2 text-xs text-muted-foreground space-y-1">
                              <p className="flex items-center gap-2">
                                <Mail className="h-3 w-3" />
                                {entry.contact.email || "No email"}
                              </p>
                              <p className="flex items-center gap-2">
                                <Phone className="h-3 w-3" />
                                {entry.contact.telephone || "No phone"}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={creatingPartner}
                              onClick={() => setPartnerPriorityContactByIndex(entry.index)}
                              className="shadow-sm hover:shadow"
                            >
                              <Star className="mr-2 h-4 w-4" />
                              Set priority
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button type="button" variant="ghost" size="icon" disabled={creatingPartner} className="h-9 w-9">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() =>
                                    updatePartnerContactNoResponseByIndex(
                                      entry.index,
                                      !entry.contact.no_response,
                                    )
                                  }
                                >
                                  {entry.contact.no_response ? "Clear no response" : "Mark no response"}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => removePartnerContactByIndex(entry.index)}
                                >
                                  Delete contact
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </div>
                    ))}
                    {otherPartnerContactEntries.length > 5 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAllPartnerContacts((prev) => !prev)}
                        className="w-full"
                      >
                        {showAllPartnerContacts
                          ? "Show less contacts"
                          : `Show all contacts (${otherPartnerContactEntries.length})`}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowPartnerForm(false);
                  resetPartnerForm();
                }}
                disabled={creatingPartner}
              >
                Cancel
              </Button>
              <Button type="button" onClick={handleCreatePartner} disabled={creatingPartner || !newPartnerBusinessType}>
                {creatingPartner ? "Creating..." : "Create Partner"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CreateNewProposal;
